import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';

const PERIODIC_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export const useLocationTracking = () => {
  const { api } = useAuth();
  const { isRunning, isPaused, runningEntry } = useTimer();
  const periodicRef = useRef(null);
  const lastSentRef = useRef(0);

  const captureAndSend = useCallback(async (captureType = 'periodic') => {
    if (!api || !navigator.geolocation) return;

    // Debounce: don't send more than once per 30 seconds
    const now = Date.now();
    if (captureType === 'periodic' && now - lastSentRef.current < 30000) return;

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const payload = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        time_entry_id: runningEntry?.id || null,
        capture_type: captureType
      };

      await api.post('/locations', payload);
      lastSentRef.current = Date.now();
    } catch (err) {
      // Silently fail — location is optional
      if (err.code !== 1) { // 1 = PERMISSION_DENIED
        console.debug('Location capture skipped:', err.message || err);
      }
    }
  }, [api, runningEntry]);

  // Capture on timer start
  useEffect(() => {
    if (isRunning && !isPaused && runningEntry) {
      captureAndSend('start');
    }
  }, [isRunning, runningEntry?.id]); // Only on new timer start

  // Periodic capture every 10 minutes while timer is running
  useEffect(() => {
    if (isRunning && !isPaused) {
      periodicRef.current = setInterval(() => {
        captureAndSend('periodic');
      }, PERIODIC_INTERVAL_MS);
    } else {
      if (periodicRef.current) {
        clearInterval(periodicRef.current);
        periodicRef.current = null;
      }
    }

    return () => {
      if (periodicRef.current) {
        clearInterval(periodicRef.current);
        periodicRef.current = null;
      }
    };
  }, [isRunning, isPaused, captureAndSend]);

  // Capture on timer stop (when isRunning goes from true to false)
  const prevRunningRef = useRef(isRunning);
  useEffect(() => {
    if (prevRunningRef.current && !isRunning) {
      captureAndSend('stop');
    }
    prevRunningRef.current = isRunning;
  }, [isRunning, captureAndSend]);

  return { captureAndSend };
};
